import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaDownload, FaChartPie, FaChartLine, FaWallet, FaMoneyBillWave, FaCalendarAlt } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';

const ReportModal = ({ isOpen, onClose, reportData }) => {
  const { currentUser } = useAuth();
  
  if (!isOpen || !reportData) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const handleDownload = () => {
    // Create a PDF document using jsPDF
    const doc = new jsPDF();
    
    try {
      // Add logo
      const logoUrl = '/cashsule_new.png';
      doc.addImage(logoUrl, 'PNG', 15, 10, 30, 30);
      
      // Title and header info
      doc.setFontSize(20);
      doc.text('Cashsule Financial Report', 50, 26);
      
      // Add report period and account info
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text('Report Period:', 15, 50);
      doc.text(`${reportData.period.start} - ${reportData.period.end}`, 50, 50);
      
      if (currentUser) {
        doc.text('Account Holder:', 15, 60);
        doc.text(currentUser.email || 'No email provided', 50, 60);
      }
      
      // Add report summary header
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text('Report Summary:', 15, 80);
      
      let yPos = 90;
      
      // Basic info in table format
      doc.autoTable({
        startY: yPos,
        margin: { left: 15 },
        head: [['Metric', 'Value']],
        body: [
          ['Total Spent', `${reportData.currencySymbol}${reportData.totalSpent.toLocaleString()}`],
          ['Average Monthly', `${reportData.currencySymbol}${reportData.avgMonthlySpend.toLocaleString()}`],
          ['Monthly Spending Trend', `${reportData.monthOverMonthChange > 0 ? '+' : ''}${reportData.monthOverMonthChange}%`]
        ],
        headStyles: { fillColor: [99, 102, 241] },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 1 && data.row.index === 2) {
            if (reportData.monthOverMonthChange > 0) {
              data.cell.styles.textColor = [239, 68, 68];
            } else if (reportData.monthOverMonthChange < 0) {
              data.cell.styles.textColor = [16, 185, 129];
            } else {
              data.cell.styles.textColor = [107, 114, 128];
            }
          }
        }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Add category breakdown
      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text('Category Breakdown', 15, yPos);
      
      yPos += 10;
      
      // Categories table
      const categoryRows = reportData.categoryBreakdown.map(cat => [
        cat.name,
        `${reportData.currencySymbol}${cat.amount.toLocaleString()}`,
        `${cat.percentage}%`
      ]);
      
      doc.autoTable({
        startY: yPos,
        margin: { left: 15 },
        head: [['Category', 'Amount', '% of Total']],
        body: categoryRows,
        headStyles: { fillColor: [99, 102, 241] },
        alternateRowStyles: { fillColor: [249, 250, 251] }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Add recommendations section
      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text('Personalised Recommendations', 15, yPos);
      
      yPos += 10;
      
      // Add recommendations
      reportData.recommendations.forEach((rec, index) => {
        doc.setFontSize(12);
        doc.setTextColor(31, 41, 55);
        doc.text(`${index + 1}. ${rec.title}`, 15, yPos);
        
        yPos += 6;
        
        doc.setFontSize(10);
        doc.setTextColor(75, 85, 99);
        
        // Split description text into multiple lines to prevent overflow
        const splitText = doc.splitTextToSize(rec.description, 180);
        doc.text(splitText, 15, yPos);
        
        yPos += (splitText.length * 5) + 8;
        
        // Check if we need to add a new page
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
      });
      
      // Add financial goals section
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text('Suggested Financial Goals', 15, yPos);
      
      yPos += 10;
      
      // Add goals in a clean, readable format
      reportData.goals.forEach((goal, index) => {
        doc.setFontSize(11);
        doc.setTextColor(31, 41, 55);
        
        // Split goal text into multiple lines to prevent overflow
        const goalText = `${index + 1}. ${goal}`;
        const splitGoal = doc.splitTextToSize(goalText, 180);
        doc.text(splitGoal, 15, yPos);
        
        yPos += (splitGoal.length * 6) + 4;
        
        // Check if we need to add a new page
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
      });
      
      // Add footer disclaimer
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(9);
      doc.setTextColor(107, 114, 128);
      const disclaimer = "Note: This report is generated based on your historical spending data and uses basic financial principles to provide recommendations. For professional financial advice, please consult with a qualified financial adviser.";
      const splitDisclaimer = doc.splitTextToSize(disclaimer, 180);
      doc.text(splitDisclaimer, 15, 275);
      
      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
        doc.text(
          'Generated by Cashsule',
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
    
    // Save the PDF
    doc.save(`financial_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Function to determine colour class based on the percentage value
  const getPercentageColorClass = (percentage) => {
    if (percentage >= 30) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    if (percentage >= 15) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-50"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-4 sm:inset-10 md:inset-16 z-50 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg shadow-xl flex flex-col"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <FaChartLine className="mr-2 text-indigo-600 dark:text-indigo-400" />
                Financial Report
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownload}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                  title="Download PDF"
                >
                  <FaDownload className="w-5 h-5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 py-6 relative bg-gray-100 dark:bg-gray-900 rounded-b-lg overflow-y-auto">
              {window.innerWidth < 768 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                    Financial Report is not available on this device.{' '}
                    <button
                      onClick={handleDownload}
                      className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                    >
                      Download instead
                    </button>
                  </p>
                </div>
              ) : (
                <div id="report-content" className="max-w-4xl mx-auto">
                  <div className="mb-8 bg-white dark:bg-gray-700 rounded-lg shadow-md overflow-hidden">
                    <div className="p-6 flex flex-col md:flex-row items-center">
                      <img src="/cashsule_new.png" alt="Cashsule Logo" className="w-16 h-16 mr-4 mb-4 md:mb-0" />
                      <div className="text-center md:text-left">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                          Financial Health Report
                        </h1>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Generated on {new Date().toLocaleDateString('en-GB')} | Period: {reportData.period.start} to {reportData.period.end}
                        </div>
                        {currentUser && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Account: {currentUser.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Overview Section */}
                  <div className="section mb-8">
                    <div className="flex items-center mb-4">
                      <FaWallet className="mr-2 text-indigo-600 dark:text-indigo-400" />
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Financial Overview
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border-l-4 border-indigo-500">
                        <div className="metric-title text-gray-500 dark:text-gray-400">Total Spent</div>
                        <div className="metric-value font-bold text-2xl text-gray-900 dark:text-white mt-2">
                          {reportData.currencySymbol}{reportData.totalSpent.toLocaleString()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border-l-4 border-purple-500">
                        <div className="metric-title text-gray-500 dark:text-gray-400">Average Monthly</div>
                        <div className="metric-value font-bold text-2xl text-gray-900 dark:text-white mt-2">
                          {reportData.currencySymbol}{reportData.avgMonthlySpend.toLocaleString()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border-l-4 border-blue-500">
                        <div className="metric-title text-gray-500 dark:text-gray-400">Monthly Spending Trend</div>
                        <div className="metric-value font-bold text-2xl mt-2">
                          <div className="flex items-center justify-left">
                            <span className={`mr-2 ${
                              reportData.monthOverMonthChange > 0 
                                ? 'text-red-500 dark:text-red-400' 
                                : reportData.monthOverMonthChange < 0 
                                  ? 'text-emerald-500 dark:text-emerald-400' 
                                  : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {reportData.monthOverMonthChange > 0 ? '↑' : reportData.monthOverMonthChange < 0 ? '↓' : '→'}&nbsp;
                              {Math.abs(reportData.monthOverMonthChange).toFixed(2)}%
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              vs last month
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Spending Analysis Section */}
                  <div className="section mb-8">
                    <div className="flex items-center mb-4">
                      <FaChartPie className="mr-2 text-indigo-600 dark:text-indigo-400" />
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Spending Analysis
                      </h2>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6">
                      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                        <h3 className="font-medium text-lg text-gray-900 dark:text-white">
                          Category Breakdown
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 dark:bg-gray-900">
                                <th className="p-3 text-left text-gray-700 dark:text-gray-300 rounded-tl-lg">Category</th>
                                <th className="p-3 text-left text-gray-700 dark:text-gray-300">Amount</th>
                                <th className="p-3 text-left text-gray-700 dark:text-gray-300 rounded-tr-lg">% of Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.categoryBreakdown.map((category, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/50' : ''}>
                                  <td className="p-3 border-t border-gray-100 dark:border-gray-700">
                                    {category.name}
                                  </td>
                                  <td className="p-3 border-t border-gray-100 dark:border-gray-700 font-medium">
                                    {reportData.currencySymbol}{category.amount.toLocaleString()}
                                  </td>
                                  <td className="p-3 border-t border-gray-100 dark:border-gray-700">
                                    <span className={`inline-block py-1 px-2 rounded-full text-xs font-medium ${getPercentageColorClass(category.percentage)}`}>
                                      {category.percentage}%
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="mt-6">
                          <div className="flex flex-wrap -mx-2">
                            {reportData.categoryBreakdown.slice(0, 5).map((category, index) => (
                              <div key={index} className="px-2 w-full sm:w-1/2 md:w-1/5 mb-4">
                                <div className="relative pt-1">
                                  <div className="flex mb-2 items-center justify-between">
                                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate mr-1">
                                      {category.name}
                                    </div>
                                    <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                                      {category.percentage}%
                                    </div>
                                  </div>
                                  <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200 dark:bg-gray-700">
                                    <div 
                                      style={{ width: `${category.percentage}%` }} 
                                      className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                        index % 5 === 0 ? 'bg-indigo-500' :
                                        index % 5 === 1 ? 'bg-purple-500' : 
                                        index % 5 === 2 ? 'bg-blue-500' : 
                                        index % 5 === 3 ? 'bg-emerald-500' : 
                                        'bg-amber-500'
                                      }`}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5">
                      <h3 className="font-medium text-lg text-gray-900 dark:text-white mb-4">
                        Spending Patterns
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                          <p className="text-gray-800 dark:text-gray-200">
                            Your highest spending category is <span className="font-semibold text-indigo-700 dark:text-indigo-400">{reportData.highestCategory.name}</span>, 
                            accounting for <span className="font-semibold text-indigo-700 dark:text-indigo-400">{reportData.highestCategory.percentage}%</span> of your total spending.
                          </p>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                          <p className="text-gray-800 dark:text-gray-200">
                            Your spending shows the most variation in <span className="font-semibold text-purple-700 dark:text-purple-400">{reportData.highestVarianceCategory.name}</span>, 
                            with spending amounts changing by up to <span className="font-semibold text-purple-700 dark:text-purple-400">{reportData.highestVarianceCategory.variance}%</span> between months.
                          </p>
                        </div>
                        
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                          <p className="text-gray-800 dark:text-gray-200">
                            Compared to previous months, your spending {
                              reportData.monthOverMonthChange > 5 
                                ? 'has increased significantly' 
                                : reportData.monthOverMonthChange < -5 
                                  ? 'has decreased significantly' 
                                  : 'has remained relatively stable'
                            }.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations Section */}
                  <div className="section mb-8">
                    <div className="flex items-center mb-4">
                      <FaMoneyBillWave className="mr-2 text-indigo-600 dark:text-indigo-400" />
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Personalised Recommendations
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {reportData.recommendations.map((recommendation, index) => (
                        <div 
                          key={index} 
                          className={`bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden ${
                            index % 4 === 0 ? 'border-t-4 border-indigo-500' : 
                            index % 4 === 1 ? 'border-t-4 border-purple-500' : 
                            index % 4 === 2 ? 'border-t-4 border-blue-500' : 
                            'border-t-4 border-emerald-500'
                          }`}
                        >
                          <div className="p-5">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
                              {index + 1}. {recommendation.title}
                            </h3>
                            <p className="text-gray-700 dark:text-gray-300">
                              {recommendation.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Financial Goals Section */}
                  <div className="section mb-8">
                    <div className="flex items-center mb-4">
                      <FaChartLine className="mr-2 text-indigo-600 dark:text-indigo-400" />
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Suggested Financial Goals
                      </h2>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {reportData.goals.map((goal, index) => (
                          <div key={index} className="border border-gray-200 dark:border-gray-700 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <div className="flex items-start">
                              <div className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full w-8 h-8 flex items-center justify-center shrink-0 mr-3">
                                <span className="font-bold">{index + 1}</span>
                              </div>
                              <p className="text-gray-800 dark:text-gray-200">{goal}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="note bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg text-sm text-gray-600 dark:text-gray-400">
                    <p><strong>Note:</strong> This report is generated based on your historical spending data and uses basic financial principles to provide recommendations. For professional financial advice, please consult with a qualified financial adviser.</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ReportModal; 